import React, { useEffect, useState, useCallback } from 'react';
import {
  Headphones, Plus, Pencil, Trash2, Loader2, ExternalLink, BarChart3, Wrench,
  X, Power, Users, MousePointerClick, Link as LinkIcon,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import StaffToolsLauncher, { iconFor } from '../../components/StaffToolsLauncher';

const ICON_OPTIONS = ['phone', 'map-pin', 'headset', 'link', 'briefcase', 'users', 'calendar', 'chart', 'wrench', 'globe'];

const EMPTY = { title: '', url: '', description: '', icon: 'link', enabled: true, order: 0, access: { roles: [], employee_ids: [] } };

export default function StaffToolsAdminTab({ api }) {
  const [sub, setSub] = useState('manage'); // manage | launch | analytics
  const [links, setLinks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, u] = await Promise.all([
        api('GET', '/admin/staff-links'),
        api('GET', '/admin/staff-links/assignable-users'),
      ]);
      setLinks(l.data.links || []);
      setUsers(u.data.users || []);
    } catch (e) {
      toast.error('Failed to load staff tools');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (link) => {
    try { await api('PATCH', `/admin/staff-links/${link.id}`, { enabled: !link.enabled }); toast.success(link.enabled ? 'Link disabled' : 'Link enabled'); load(); }
    catch { toast.error('Update failed'); }
  };
  const remove = async (link) => {
    if (!window.confirm(`Delete "${link.title}"? This also stops it appearing for staff.`)) return;
    try { await api('DELETE', `/admin/staff-links/${link.id}`); toast.success('Link deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-5" data-testid="staff-tools-admin-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Headphones size={20} className="text-indigo-600" /> EntoRid Staff Tools</h2>
          <p className="text-sm text-slate-500">Manage external staff links under EntoRid branding, control who can access them, and track clicks.</p>
        </div>
        {sub === 'manage' && <Button onClick={() => setEditing(EMPTY)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="staff-link-new"><Plus size={15} className="mr-1" /> Add Link</Button>}
      </div>

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
        {[['manage', 'Manage Links', Wrench], ['launch', 'Launch', ExternalLink], ['analytics', 'Click Analytics', BarChart3]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setSub(id)} className={`px-4 py-1.5 rounded-md text-sm font-semibold flex items-center gap-1.5 ${sub === id ? 'bg-indigo-600 text-white' : 'text-slate-600'}`} data-testid={`staff-subtab-${id}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div>
      ) : sub === 'manage' ? (
        <div className="space-y-3" data-testid="staff-links-manage">
          {links.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200"><LinkIcon className="mx-auto text-slate-300 mb-2" size={36} /><p className="text-slate-500">No links yet. Add your first staff tool.</p></div>
          ) : links.map((link) => {
            const Icon = iconFor(link.icon);
            const grantedCount = (link.access?.employee_ids?.length || 0) + (link.access?.roles?.length || 0);
            return (
              <div key={link.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`staff-link-row-${link.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${link.enabled ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-400'}`}><Icon size={18} /></div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-semibold text-slate-900"><span className="truncate">{link.title}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${link.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>{link.enabled ? 'ENABLED' : 'DISABLED'}</span>
                    </div>
                    <div className="text-xs text-slate-400 truncate">{link.url}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Users size={11} /> {grantedCount === 0 ? 'Super Admin only' : `${grantedCount} grant(s)`}</span>
                      <span className="inline-flex items-center gap-1"><MousePointerClick size={11} /> {link.click_count || 0} clicks</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(link)} data-testid={`staff-link-edit-${link.id}`}><Pencil size={13} className="mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => toggle(link)} data-testid={`staff-link-toggle-${link.id}`}><Power size={13} /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(link)} className="text-rose-600 hover:text-rose-700" data-testid={`staff-link-delete-${link.id}`}><Trash2 size={13} /></Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : sub === 'launch' ? (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="staff-links-launch">
          <StaffToolsLauncher api={api} />
        </div>
      ) : (
        <AnalyticsPanel api={api} links={links} />
      )}

      {editing && <LinkEditor api={api} initial={editing} users={users} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function LinkEditor({ api, initial, users, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...EMPTY, ...initial, access: { roles: [...(initial.access?.roles || [])], employee_ids: [...(initial.access?.employee_ids || [])] } });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleRole = (role) => setForm((f) => {
    const has = f.access.roles.includes(role);
    return { ...f, access: { ...f.access, roles: has ? f.access.roles.filter((r) => r !== role) : [...f.access.roles, role] } };
  });
  const toggleEmp = (id) => setForm((f) => {
    const has = f.access.employee_ids.includes(id);
    return { ...f, access: { ...f.access, employee_ids: has ? f.access.employee_ids.filter((e) => e !== id) : [...f.access.employee_ids, id] } };
  });

  const save = async () => {
    if (!form.title || form.title.trim().length < 2) return toast.error('Title is required');
    if (!form.url || form.url.trim().length < 4) return toast.error('A valid URL is required');
    setSaving(true);
    const payload = {
      title: form.title.trim(), url: form.url.trim(), description: form.description || null,
      icon: form.icon, enabled: form.enabled, order: Number(form.order) || 0, access: form.access,
    };
    try {
      if (isNew) await api('POST', '/admin/staff-links', payload);
      else await api('PATCH', `/admin/staff-links/${initial.id}`, payload);
      toast.success(isNew ? 'Link added' : 'Link updated');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const admins = users.filter((u) => u.role === 'admin');
  const employees = users.filter((u) => u.role === 'employee');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="staff-link-editor">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between bg-indigo-700 text-white px-5 py-3.5 rounded-t-2xl">
          <div className="font-bold">{isNew ? 'Add Staff Link' : `Edit: ${initial.title}`}</div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/15" data-testid="staff-link-editor-close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><Label>Title (EntoRid-branded) *</Label><Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g., EntoRid Telecalling" data-testid="staff-link-title" /></div>
          <div><Label>URL *</Label><Input value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://…" data-testid="staff-link-url" /></div>
          <div><Label>Description</Label><Input value={form.description || ''} onChange={(e) => set('description', e.target.value)} data-testid="staff-link-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Icon</Label>
              <select value={form.icon} onChange={(e) => set('icon', e.target.value)} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="staff-link-icon">
                {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => set('order', e.target.value)} data-testid="staff-link-order" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} className="w-4 h-4" data-testid="staff-link-enabled" /> Enabled (visible to granted staff)</label>

          <div className="pt-2 border-t border-slate-100">
            <Label className="text-slate-700">Access — by role</Label>
            <p className="text-[11px] text-slate-400 mb-2">Super Admin always has access. Grant whole roles or pick individuals below.</p>
            <div className="flex gap-2">
              {['admin', 'employee'].map((r) => (
                <button key={r} type="button" onClick={() => toggleRole(r)} data-testid={`staff-access-role-${r}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form.access.roles.includes(r) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'}`}>
                  All {r}s
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-slate-700">Access — individual staff</Label>
            <div className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="staff-access-users">
              {[...admins, ...employees].length === 0 ? <div className="p-3 text-xs text-slate-400">No staff accounts found.</div> :
                [...admins, ...employees].map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`staff-access-user-${u.id}`}>
                    <input type="checkbox" checked={form.access.employee_ids.includes(u.id)} onChange={() => toggleEmp(u.id)} className="w-4 h-4" />
                    <span className="flex-1 truncate">{u.name} <span className="text-slate-400">· {u.role}{u.employee_code ? ` · ${u.employee_code}` : ''}</span></span>
                  </label>
                ))}
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 flex justify-end gap-2 bg-white border-t border-slate-100 px-5 py-3 rounded-b-2xl">
          <Button variant="outline" onClick={onClose} data-testid="staff-link-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="staff-link-save">
            {saving ? <><Loader2 size={15} className="animate-spin mr-1" /> Saving…</> : (isNew ? 'Add Link' : 'Save Changes')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AnalyticsPanel({ api, links }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', link_id: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      if (filters.link_id) params.set('link_id', filters.link_id);
      const qs = params.toString();
      const res = await api('GET', `/admin/staff-links/analytics${qs ? `?${qs}` : ''}`);
      setData(res.data);
    } catch (e) { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  }, [api, filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="staff-analytics">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className="h-9 w-40" data-testid="staff-analytics-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className="h-9 w-40" data-testid="staff-analytics-to" /></div>
        <div>
          <Label className="text-xs">Link</Label>
          <select value={filters.link_id} onChange={(e) => setFilters((f) => ({ ...f, link_id: e.target.value }))} className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="staff-analytics-link">
            <option value="">All links</option>
            {links.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
          </select>
        </div>
        {(filters.date_from || filters.date_to || filters.link_id) && (
          <Button variant="outline" size="sm" onClick={() => setFilters({ date_from: '', date_to: '', link_id: '' })} data-testid="staff-analytics-clear">Clear</Button>
        )}
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl bg-indigo-600 text-white p-4" data-testid="staff-analytics-total"><div className="text-xs uppercase tracking-wide text-indigo-200">Total Clicks</div><div className="text-3xl font-extrabold mt-1">{data.total_clicks}</div></div>
            <div className="rounded-xl bg-white border border-slate-200 p-4"><div className="text-xs uppercase tracking-wide text-slate-400">Links Used</div><div className="text-3xl font-extrabold text-slate-900 mt-1">{data.per_link.length}</div></div>
            <div className="rounded-xl bg-white border border-slate-200 p-4"><div className="text-xs uppercase tracking-wide text-slate-400">Active Staff</div><div className="text-3xl font-extrabold text-slate-900 mt-1">{data.per_employee.length}</div></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase">Clicks per Link</div>
              <div className="divide-y divide-slate-100">
                {data.per_link.length === 0 ? <div className="p-4 text-sm text-slate-400">No clicks in this range.</div> :
                  data.per_link.map((r) => (
                    <div key={r.link_id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`staff-analytics-perlink-${r.link_id}`}>
                      <span className="text-slate-700">{r.link_title}</span><span className="font-bold text-indigo-700">{r.count}</span>
                    </div>
                  ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase">Clicks per Employee</div>
              <div className="divide-y divide-slate-100">
                {data.per_employee.length === 0 ? <div className="p-4 text-sm text-slate-400">No clicks in this range.</div> :
                  data.per_employee.map((r) => (
                    <div key={r.user_id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`staff-analytics-peremp-${r.user_id}`}>
                      <span className="text-slate-700">{r.user_name} <span className="text-slate-400">· {r.user_role}</span></span><span className="font-bold text-indigo-700">{r.count}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase">Recent Clicks</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-400 uppercase"><tr><th className="px-4 py-2 text-left">When</th><th className="px-4 py-2 text-left">Employee</th><th className="px-4 py-2 text-left">Link</th></tr></thead>
                <tbody className="divide-y divide-slate-100" data-testid="staff-analytics-recent">
                  {data.recent.length === 0 ? <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">No clicks recorded.</td></tr> :
                    data.recent.slice(0, 100).map((c) => (
                      <tr key={c.id}>
                        <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{new Date(c.clicked_at).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-2 text-slate-700">{c.user_name} <span className="text-slate-400">· {c.user_role}</span></td>
                        <td className="px-4 py-2 text-slate-600">{c.link_title}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
